import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import { viewCuratedStyleProps } from "../theme/main";
import { CuratedAngleSelector } from "./CuratedAngleSelector";
import { FadeViewTransition } from "./FadeViewTransition";

export interface CuratedConfig {
  angles: string[];
  basePath?: string;
  options: Record<string, Record<string, string>>;
  selectedOptions?: Record<string, string>;
}

function getDefaultSelections(options: CuratedConfig["options"]) {
  return Object.entries(options).reduce<Record<string, string>>(
    (selections, [optionType, optionValues]) => {
      const optionNames = Object.keys(optionValues);
      const defaultOption =
        optionNames.find((optionName) => optionName === "Default") ||
        optionNames.find((optionName) => optionName === "None") ||
        optionNames[0];

      if (defaultOption) {
        selections[optionType] = defaultOption;
      }

      return selections;
    },
    {},
  );
}

function getOptionCacheKey(optionType: string, optionName: string) {
  return `${optionType}:${optionName}`;
}

function getSequenceCacheKey(
  optionType: string,
  optionName: string,
  pathPattern: string,
) {
  return `${getOptionCacheKey(optionType, optionName)}:${pathPattern}`;
}

const ViewCurated: React.FC<{ config: CuratedConfig }> = ({ config }) => {
  const [currentAngle, setCurrentAngle] = useState(0);
  const [isPreloading, setIsPreloading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [cacheVersion, setCacheVersion] = useState(0);

  const imageCache = useRef<Map<string, HTMLImageElement[]>>(new Map());
  const selections = useMemo(
    () => ({
      ...getDefaultSelections(config.options),
      ...config.selectedOptions,
    }),
    [config.options, config.selectedOptions],
  );
  const optionEntries = useMemo(
    () =>
      Object.entries(config.options).flatMap(([optionType, optionValues]) =>
        Object.entries(optionValues).map(([optionName, pathPattern]) => ({
          optionType,
          optionName,
          cacheKey: getSequenceCacheKey(optionType, optionName, pathPattern),
          pathPattern,
        })),
      ),
    [config.options],
  );
  const selectedCacheKeys = useMemo(
    () =>
      optionEntries
        .filter((entry) => selections[entry.optionType] === entry.optionName)
        .map((entry) => entry.cacheKey),
    [optionEntries, selections],
  );
  const firstOptionPath = Object.values(config.options)
    .flatMap((optionValues) => Object.values(optionValues))
    .find(Boolean);
  const previewPath = config.basePath || firstOptionPath || "";
  const firstFrameSrc = previewPath.replace("{frame}", "0000");
  const {
    preloaderRootStyle,
    preloaderPreviewImageStyle,
    preloaderOverlayStyle,
    preloaderProgressStyle,
    rootStyle,
    angleNavigationStyle,
    arrowButtonStyle,
    leftArrowButtonStyle,
    rightArrowButtonStyle,
  } = viewCuratedStyleProps;

  // 1. PRELOADER (Same professional logic)
  useEffect(() => {
    const frameCount = config.angles.length;
    const selectedCacheKeySet = new Set(selectedCacheKeys);
    const selectedEntries = optionEntries.filter((entry) =>
      selectedCacheKeySet.has(entry.cacheKey),
    );
    const backgroundEntries = optionEntries.filter(
      (entry) => !selectedCacheKeySet.has(entry.cacheKey),
    );
    const totalToLoad =
      ((config.basePath ? 1 : 0) + optionEntries.length) * frameCount;
    let loaded = 0;

    const loadSequence = async (name: string, pathPattern: string) => {
      if (imageCache.current.has(name)) {
        loaded += frameCount;
        setProgress(Math.round((loaded / totalToLoad) * 100));
        return;
      }

      const frames: HTMLImageElement[] = [];

      for (let i = 0; i < frameCount; i++) {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = pathPattern.replace("{frame}", "000" + i.toString());
        await img.decode();
        frames.push(img);
        loaded++;
        setProgress(Math.round((loaded / totalToLoad) * 100));
      }

      imageCache.current.set(name, frames);
      setCacheVersion((version) => version + 1);
    };

    const init = async () => {
      if (totalToLoad === 0) {
        setIsPreloading(false);
        return;
      }

      if (config.basePath) {
        await loadSequence("base", config.basePath);
      }

      for (const { cacheKey, pathPattern } of selectedEntries) {
        await loadSequence(cacheKey, pathPattern);
      }

      setIsPreloading(false);

      for (const { cacheKey, pathPattern } of backgroundEntries) {
        await loadSequence(cacheKey, pathPattern);
      }
    };
    init();
  }, [config, optionEntries, selectedCacheKeys]);

  if (isPreloading) {
    return (
      <Box
        sx={{
          ...preloaderRootStyle,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {firstFrameSrc && (
          <Box
            component="img"
            src={firstFrameSrc}
            alt=""
            sx={{
              ...preloaderPreviewImageStyle,
              position: "absolute",
              inset: 0,
            }}
          />
        )}
        <Box
          sx={{
            ...preloaderOverlayStyle,
            position: "absolute",
            // inset: 0,
          }}
        />
        <Typography sx={preloaderProgressStyle}>{progress + "%"}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...rootStyle,
        position: "relative",
      }}
    >
      <FadeViewTransition
        activeKey={String(currentAngle)}
        durationMs={180}
        fitParent
        views={config.angles.map((angle, index) => ({
          key: String(index),
          children: (
            <CuratedAngleFrame
              angleIndex={index}
              cacheVersion={cacheVersion}
              imageCache={imageCache.current}
              selectedCacheKeys={selectedCacheKeys}
            />
          ),
        }))}
      />

      {/* ANGLE NAVIGATION (Bottom Center - Upper Row) */}
      <Box
        sx={{
          ...angleNavigationStyle,
          position: "absolute",
          margin: "auto",
        }}
      >
        <CuratedAngleSelector
          angles={config.angles}
          activeAngleIndex={currentAngle}
          onChange={setCurrentAngle}
        />
      </Box>

      {/* ARROWS (Kept as secondary navigation) */}
      <IconButton
        onClick={() =>
          setCurrentAngle(
            (p) => (p - 1 + config.angles.length) % config.angles.length,
          )
        }
        sx={{
          ...arrowButtonStyle,
          ...leftArrowButtonStyle,
          position: "absolute",
          transform: "translateY(-50%)",
        }}
      >
        <ArrowBackIosNew />
      </IconButton>
      <IconButton
        onClick={() => setCurrentAngle((p) => (p + 1) % config.angles.length)}
        sx={{
          ...arrowButtonStyle,
          ...rightArrowButtonStyle,
          position: "absolute",
          transform: "translateY(-50%)",
        }}
      >
        <ArrowForwardIos />
      </IconButton>
    </Box>
  );
};

export default ViewCurated;

type CuratedAngleFrameProps = {
  angleIndex: number;
  cacheVersion: number;
  imageCache: Map<string, HTMLImageElement[]>;
  selectedCacheKeys: string[];
};

function CuratedAngleFrame({
  angleIndex,
  cacheVersion,
  imageCache,
  selectedCacheKeys,
}: CuratedAngleFrameProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const drawAngle = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const baseFrames = imageCache.get("base");
    const selectedFrames = selectedCacheKeys
      .map((cacheKey) => imageCache.get(cacheKey)?.[angleIndex])
      .filter((image): image is HTMLImageElement => Boolean(image));
    const sizingImage = baseFrames?.[angleIndex] || selectedFrames[0];

    if (!sizingImage) return;

    canvas.width = sizingImage.width;
    canvas.height = sizingImage.height;

    if (baseFrames?.[angleIndex]) {
      ctx.drawImage(baseFrames[angleIndex], 0, 0);
    }

    for (const optionImg of selectedFrames) {
      ctx.drawImage(optionImg, 0, 0);
    }
  }, [angleIndex, cacheVersion, imageCache, selectedCacheKeys]);

  useEffect(() => {
    drawAngle();
  }, [drawAngle]);

  const { canvasStyle } = viewCuratedStyleProps;

  return (
    <canvas
      ref={canvasRef}
      style={{
        ...canvasStyle,
        display: "block",
      }}
    />
  );
}
